import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";

const Burst = () => (
  <span className="pointer-events-none absolute inset-0" aria-hidden>
    {Array.from({ length: 10 }, (_, i) => (
      <Heart key={i} className="thank-heart absolute left-1/2 top-1/2 w-4 h-4 text-rose-400" fill="currentColor" style={{ "--a": `${i * 36}deg`, animationDelay: `${(i % 3) * 60}ms` }} />
    ))}
  </span>
);

const ThankFans = ({ s, label }) => s.thanks > 0 && (
  <div className="flex items-center justify-center gap-2" data-testid="newest-supporter-fans">
    <div className="flex -space-x-2">
      {(s.fans || []).slice(0, 6).map((f) => (
        <Link key={f.user_id} to={`/u/${f.user_id}`} title={`${f.name} sagt Danke`} data-testid={`newest-supporter-fan-${f.user_id}`} className="rounded-full ring-2 ring-[#c9962a] hover:-translate-y-0.5 transition-transform">
          <UserAvatar userId={f.user_id} name={f.name} picture={f.picture} size="w-7 h-7" text="text-[10px]" crown={false} noStory />
        </Link>
      ))}
    </div>
    <span className="text-xs font-semibold text-[#efdcae]" data-testid="newest-supporter-thanks">{label}</span>
  </div>
);

const ThankButton = ({ s, setS }) => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [burst, setBurst] = useState(0);
  const label = s.thanks === 1 ? "1 Mitglied sagt Danke" : `${s.thanks} Mitglieder sagen Danke`;
  if (user?.user_id === s.user_id) return <div data-testid="newest-supporter-thanks-own">{s.thanks ? <ThankFans s={s} label={label} /> : <p className="text-sm text-[#efdcae]">Noch hat dir niemand gedankt – gleich geht's los 💖</p>}</div>;
  const thank = async () => {
    if (s.thanked || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/supporters/${s.user_id}/thank`);
      setS((x) => ({ ...x, ...data }));
      setBurst((b) => b + 1);
      if (data.bonus) setUser((u) => ({ ...u, points: (u.points || 0) + data.bonus }));
      toast.success(data.bonus ? `Danke an ${s.name} gesendet 💖 +${data.bonus} Punkte für dich` : `Danke an ${s.name} gesendet 💖`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button onClick={thank} disabled={busy} data-testid="newest-supporter-thank" data-thanked={s.thanked || undefined}
        className={`relative h-12 px-6 rounded-full inline-flex items-center gap-2 font-extrabold text-sm transition-transform active:scale-90 ${s.thanked ? "bg-white/10 text-[#f6e3a6] border border-[#c9962a]" : "vip-gold-metal shadow-lg hover:scale-105"}`}>
        {burst > 0 && <Burst key={burst} />}
        <Heart className={`w-5 h-5 ${s.thanked ? "text-rose-400" : "heart-beat"}`} fill={s.thanked ? "currentColor" : "none"} />
        {s.thanked ? "Danke gesendet" : "Danke sagen"}
        {!s.thanked && <span className="text-[11px] font-bold opacity-80" data-testid="newest-supporter-bonus">+5 Punkte</span>}
      </button>
      <ThankFans s={s} label={label} />
    </div>
  );
};

export function NewestSupporterCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/supporters/newest").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s || new Date(s.until) < new Date()) return null;
  return (
    <div data-testid="newest-supporter-card" className="newest-sup relative overflow-hidden rounded-3xl px-5 py-7 text-center fade-up">
      <span className="newest-sup-rays" aria-hidden />
      <span className="newest-sup-sheen" aria-hidden />
      <div className="relative flex flex-col items-center gap-3">
        <p className="text-[11px] font-extrabold tracking-[0.3em] text-[#f6e3a6] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> NEUER PROJEKT UNTERSTÜTZER</p>
        <Link to={`/u/${s.user_id}`} className="flex flex-col items-center gap-3" data-testid="newest-supporter-link">
          <div className="relative mt-5">
            <span className="newest-sup-halo" aria-hidden />
            <span className="relative block rounded-full p-[4px] newest-sup-ring">
              <UserAvatar userId={s.user_id} name={s.name} picture={s.picture} size="w-24 h-24" text="text-3xl" crown={false} noStory />
            </span>
            <Heart className="absolute -top-5 left-1/2 -translate-x-1/2 w-9 h-9 text-rose-300 drop-shadow-[0_0_10px_rgba(253,164,175,.9)]" fill="currentColor" />
          </div>
          <p className="font-display text-2xl font-extrabold vip-gold-title" data-testid="newest-supporter-name">{s.name}</p>
        </Link>
        {s.greeting && <p className="text-sm italic text-[#f3dfa8] max-w-xs">„{s.greeting}“</p>}
        <p className="text-sm text-[#efdcae]">Danke, dass du unsere Community unterstützt! 💖</p>
        <ThankButton s={s} setS={setS} />
      </div>
    </div>
  );
}
